
/* eslint-disable max-len */
import ErrorRMW from 'app/shared/model/ErrorRMW';
import Constants from 'app/shared/constants';
import SelectPropertyConstants from 'app/shared/constants/SelectPropertyConstants';
import queryUtils from 'app/shared/utils/queryUtils';
import requester from 'app/shared/utils/requester';
import StringConstants from 'app/shared/constants/StringConstants';

import {
    FILE_PROXY_PATH,
    RENTAL_GROWTH_PROXY_PATH,
    RENTAL_GROWTH_API_V1_PREFIX,
    RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX,
    RENTAL_MANAGER_API_V1_PREFIX,
    RENTAL_MANAGER_API_V2_PREFIX,
} from 'app/shared/constants/RouteConstants';

const { GET, POST } = Constants;

const api = {
    claims: {
        getExclusivePropertiesForAllUnverified() {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/getExclusivePropertiesForAllUnverified`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                    })
                );
            };
        },
        deleteClaimMethod(type, value) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/delete`;
                return dispatch(
                    requester({
                        method: POST,
                        endpoint,
                        queryParams: {
                            type,
                            value,
                        },
                    })
                );
            };
        },
        deleteAllUnverifiedClaimMethods() {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/deleteAllUnverified`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                    }),
                );
            };
        },
        getClaimMethods() {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/list`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                    })
                );
            };
        },
        addContactMethod(type, value) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/add`;
                return dispatch(
                    requester({
                        method: POST,
                        endpoint,
                        queryParams: {
                            type,
                            value,
                        },
                    }),
                );
            };
        },
        propertyLookup(type, value) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/propertyLookup`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                        queryParams: {
                            type,
                            value,
                        },
                    }),
                );
            };
        },
        propertyLookupAll({ includeVerified = true, includeUnverified = true }) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/propertyLookupAll`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                        queryParams: {
                            includeVerified,
                            includeUnverified,
                        },
                    }),
                );
            };
        },
        getExclusiveProperties(type, value) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/claims/getExclusiveProperties`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                        queryParams: {
                            type,
                            value,
                        },
                    }),
                );
            };
        },
    },
    multibrandSupport: {
        appConfig(params) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/appConfig`;
                const { appName = 'zrm_web' } = params;

                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                        queryParams: {
                            appName,
                        },
                    }),
                );
            };
        },
    },
    notifications: {
        /*
         * notifications.register
         *
         * @deviceId (String) - subscription response from pushManager
         */
        register({ deviceId }) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/notifications/register`;
            const deviceName = navigator.userAgent || '';
            const deviceType = 'rental_manager_browser';

            return requester({
                method: POST,
                endpoint,
                queryParams: {
                    deviceId,
                    deviceType,
                    deviceName,
                },
            });
        },

        /*
         * notifications.unregister
         *
         * @deviceId (String) - subscription response from pushManager
         */
        unregister({ deviceId }) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/notifications/unregister`;
            const deviceType = 'rental_manager_browser';

            return requester({
                method: POST,
                endpoint,
                queryParams: {
                    deviceId,
                    deviceType,
                },
            });
        },
        getSettings() {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/notifications/getNotificationSettings`;

            return requester({
                method: GET,
                endpoint,
                queryParams: {},
            });
        },
        updateSettings(bodyParams) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/notifications/updateNotificationSettings`;

            return requester({
                method: POST,
                endpoint,
                body: bodyParams,
            });
        },
    },
    properties: {
        address: {
            autocomplete(partialAddress) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/autocomplete`;

                return requester({
                    method: GET,
                    endpoint,
                    queryParams: {
                        partialAddress,
                    },
                    timeout: 5000,
                });
            },
            comps(zpid) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/comps`;

                return requester({
                    method: GET,
                    endpoint,
                    queryParams: {
                        zpid,
                    },
                });
            },
            details(addressData) {
                const queryParams = {
                    fullAddress: addressData.address,
                };
                if (addressData.unit) {
                    queryParams.unit = addressData.unit;
                }

                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/details`;

                return requester({
                    method: GET,
                    endpoint,
                    queryParams,
                });
            },
            verification(addressData) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/verification`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                fullAddress: addressData.address,
                                unit: addressData.unit,
                            },
                        }),
                    );
                };
            },
            updateAddress(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/address`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId: data.propertyId,
                                fullAddress: data.fullAddress,
                                unitNumber: data.unitNumber,
                            },
                        }),
                    );
                };
            },
        },

        locations: {
            getRestrictions(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/locations/restrictions`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                fullAddress: data.address,
                                unit: data.unit,
                                listingTypeCode: data.listingTypeCode,
                                propertyTypeCode: data.propertyTypeCode,
                                listingAlias: data.listingAlias,
                            },
                        }),
                    );
                };
            },
            getPublicRestrictions(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/properties/locations/restrictions`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                fullAddress: data.address,
                                unit: data.unit,
                                listingTypeCode: data.listingTypeCode,
                                propertyTypeCode: data.propertyTypeCode,
                                listingAlias: data.listingAlias,
                            },
                        }),
                    );
                };
            },
        },

        specialOffers: {
            /**
             * @queryParams
             * listingId - listing id of the property
             * includeListingDetails -  if true, displays the entire listing object in response.listingDetails
             *
             * @bodyParams
             * offerIdEncoded : String aliasId of the offer (if updating existing, blank if new
             * description : description of offer (required for creation)
             * startDate : start date of offer YYYY-MM-D
             * endDate : end date of offer YYYY-MM-DD
             *
             * @returns {Function}
             */
            createOrUpdate(bodyParams, queryParams) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/specialOffers/createOrUpdate`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body: bodyParams,
                            queryParams,
                        }),
                    );
                };
            },

            /**
             *
             * @param queryParams
             * listingId - listing id of the property
             * offerIdEncoded - alias id of the offer (from create,update endpoint, details endpoint response)
             *
             * @returns {Function}
             */
            delete(queryParams) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/specialOffers/delete`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams,
                        }),
                    );
                };
            },
        },
    },
    users: {
        signUrl(url, params = {}) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/googleMaps`;
                const { brandName } = params;

                return dispatch(
                    requester({
                        method: POST,
                        endpoint,
                        body: { url },
                        queryParams: { brandName },
                    }),
                );
            };
        },

        applications: {
            enableApplications(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/applications/enableApplications`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: params,
                        }),
                    );
                };
            },
            inviteToApply(body = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/handleApplicationInvitation`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            getSummary(queryParams = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/applications`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams,
                        }),
                    );
                };
            },
        },
        contacts: {
            verifier: {
                confirm(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/confirm`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    contact: data.phone,
                                    contactType: 'phone',
                                    verificationCode: data.verificationCode,
                                },
                            }),
                        );
                    };
                },
                send(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/send`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    contact: data.phone,
                                    contactType: 'phone',
                                    verificationType: data.verificationType,
                                },
                            }),
                        );
                    };
                },
                verified(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/verified`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    contact: data.phone,
                                    contactType: 'phone',
                                },
                            }),
                        );
                    };
                },
            },
        },
        // Used for retargeting purposes
        trackEvent({ trackingEvent }) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/tracking`;

                return dispatch(
                    requester({
                        method: 'get',
                        endpoint,
                        queryParams: {
                            trackingEvent,
                        },
                    }),
                );
            };
        },
        getUser({ pageWithSensitiveInformation = false, includeSummary = false } = {}) {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/get`;

                return dispatch(
                    requester({
                        method: 'get',
                        endpoint,
                        queryParams: {
                            pageWithSensitiveInformation,
                            includeSummary,
                        },
                    }),
                );
            };
        },
        ip(skipClientProxy) {
            const endpoint = 'rental-manager-api/datarouter/ipDetection';

            return requester({
                method: 'get',
                endpoint,
                skipClientProxy,
            });
        },
        loginUser({ password, email, ap }) {
            return (dispatch) => {
                const endpoint = 'zgLogin/user/account/services/Login.htm';

                return dispatch(
                    requester({
                        method: POST,
                        body: queryUtils.stringify({
                            password, email, ap, authToken: '',
                        }),
                        header: {
                            contentType: 'application/x-www-form-urlencoded',
                        },
                        endpoint,
                    }),
                );
            };
        },
        register({ password, email, ap }) {
            return (dispatch) => {
                const endpoint = 'zgLogin/user/account/services/Register.htm';

                return dispatch(
                    requester({
                        method: POST,
                        body: {
                            password,
                            email,
                            ap,
                        },
                        endpoint,
                    }),
                );
            };
        },
        getSettings() {
            return (dispatch) => {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/userSettings/get`;

                return dispatch(
                    requester({
                        method: 'get',
                        endpoint,
                    }),
                );
            };
        },

        // TODO: update endpoints to go through rental-manager-api
        // 2fa
        twoFactorAuth: {
            enableAndRequestCode({ type, phone }) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/twoFactorAuth/enableAndRequestCode`;

                    return dispatch(
                        requester({
                            method: 'get',
                            endpoint,
                            queryParams: {
                                type,
                                phone,
                            },
                        }),
                    );
                };
            },
            requestCode(type) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/twoFactorAuth/requestCode`;

                    return dispatch(
                        requester({
                            method: 'get',
                            endpoint,
                            queryParams: {
                                type,
                            },
                        }),
                    );
                };
            },
            verifyCode(code) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/twoFactorAuth/verifyCode`;

                    return dispatch(
                        requester({
                            method: 'get',
                            endpoint,
                            queryParams: {
                                code,
                            },
                        }),
                    );
                };
            },
            disable() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/userSettings/disableTwoFactorAuthentication`;

                    return dispatch(
                        requester({
                            method: 'get',
                            endpoint,
                        }),
                    );
                };
            },
        },
        intention: {
            insertAction(params) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/intention/insertAction`;

                    return dispatch(
                        requester({
                            method: POST,
                            body: params,
                            endpoint,
                        })
                    );
                };
            },
        },

        referrals: {
            add(params) {
                return (dispatch) => {
                    const { code, type = 'paidInclusionSales' } = params;
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/referrals/add`;
                    let hasError = false;
                    let message = null;

                    // check for required params
                    if (!code) {
                        hasError = true;
                        message = 'code is required';
                    }

                    // check if code is alphanumeric
                    if (hasError === false && queryUtils.isAlphaNumeric(code) === false) {
                        hasError = true;
                        message = 'code must be alphanumeric';
                    }

                    if (hasError) {
                        return Promise.reject(ErrorRMW({
                            message,
                            data: params,
                        }));
                    }

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                type,
                                code,
                            },
                        }),
                    );
                };
            },
        },

        reportSpam(params) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/report/spamInquiry`;

            return requester({
                method: GET,
                endpoint,
                queryParams: params,
            });
        },

        // lead
        leads: {
            /**
             * Get all user leads
             *
             * @param data
             * @param {string} data.startDateYyyyMmDd - start date
             * @param {string} data.endDateYyyyMmDd - end date
             *
             * @returns dispatch
             * */
            allLeads(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/listingConversation/getLatestConversations`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: data,
                        }),
                    );
                };
            },
            lead(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/details`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                inquiryId: data.leadId,
                                includeListingDetails: data.includeListingDetails,
                            },
                        }),
                    );
                };
            },
            // update inquiry status
            updateLeadStatus(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/inbox/status`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                inquiryId: data.leadId,
                                read: data.read,
                            },
                        }),
                    );
                };
            },
            // create fake lead for listing
            createFakeLeadForListing(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/devTools/createFakeLeadForListing`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                listingId: data.listingId,
                                name: data.contactName,
                                email: data.contactEmail,
                                message: data.message,
                                isPhoneLead: data.isPhoneLead,
                            },
                        }),
                    );
                };
            },
            getConversationCountsForListing({ listingAlias } = {}) {
                /**
                 * Get lead counts by listing
                 * @param {string} [listingAlias] - listingId or aliasEncoded
                 * */
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/listingConversation/getConversationCountsForListing`;
                    let hasError = false;
                    let message = null;

                    if (!listingAlias) {
                        hasError = true;
                        message = 'listingAlias is required';
                    }

                    if (hasError) {
                        return Promise.reject(ErrorRMW({
                            message,
                            data: listingAlias,
                        }));
                    }

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: { listingAlias },
                        }),
                    );
                };
            },
        },
        reviews: {
            reviewsForListing(params = {}) {
                /**
                 * Get reviews for a given listing
                 * @param required {string} [feedListingId=''] - Feed listing id
                 * @param {string} [feedId=''] - Feed id
                 * @param {int} [start=0] - Start index for results
                 * @param {int} [limit=20] - Maximum number of results to return
                 * */
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/reviews/reviewsForListing`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                listingId: params.listingId,
                                start: params.start,
                                limit: params.limit,
                                searchText: params.searchText,
                                hideReviewsWithResponses: params.hideReviewsWithResponses,
                                sort: params.sort,
                            },
                        }),
                    );
                };
            },
            report(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/reviews/report`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                reviewId: data.reviewId,
                                reviewType: data.reviewType,
                                reason: data.reason,
                                comment: data.comment,
                            },
                        }),
                    );
                };
            },
            response: {
                create(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/reviews/response/create`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    reviewId: data.reviewId,
                                    reviewType: data.reviewType,
                                    message: data.message,
                                },
                            }),
                        );
                    };
                },
                update(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/reviews/response/update`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    reviewResponseId: data.reviewResponseId,
                                    reviewId: data.reviewId,
                                    reviewType: data.reviewType,
                                    message: data.message,
                                },
                            }),
                        );
                    };
                },
                delete(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/reviews/response/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    reviewResponseId: data.reviewResponseId,
                                    reviewType: data.reviewType,
                                },
                            }),
                        );
                    };
                },
            },
        },
        billing: {
            getInvoices(body) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoices/getInvoices`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            downloadInvoices(body) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoices/downloadInvoices`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            getInvoice(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoices/getInvoice`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                invoiceNumber: params.invoiceNumber,
                            },
                        }),
                    );
                };
            },
            getZuoraBillingFilterOptions() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoices/getZuoraBillingFilterOptions`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            invoicePayment: {
                getAccountPaymentSettingsForUser() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/getAccountPaymentSettingsForUser`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                getStartPaymentMethodSessionForAccount(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/getStartPaymentMethodSessionForAccount`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                },
                            }),
                        );
                    };
                },
                getPaymentMethodsForUser() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/getPaymentMethodsForUser`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                userHasPaymentMethods() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/userHasPaymentMethods`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                getPaymentMethodsForAccount(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/getPaymentMethodsForAccount`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                },
                            }),
                        );
                    };
                },
                deletePaymentMethod(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/deletePaymentMethod`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                    paymentMethodId: params.paymentMethodId,
                                },
                            }),
                        );
                    };
                },
                pay(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/makeInvoicePayment`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    invoiceNumber: params.invoiceNumber,
                                    paymentMethodId: params.paymentMethodId,
                                    partialPayment: params.partialPayment,
                                    amount: params.amount,
                                },
                            }),
                        );
                    };
                },
                saveDefaultPaymentMethod(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/saveDefaultPaymentMethod`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                    defaultPaymentMethodId: params.defaultPaymentMethodId,
                                    autopayEnabled: params.autopayEnabled,
                                },
                            }),
                        );
                    };
                },
                removeAutopay(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/removeAutopay`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                },
                            }),
                        );
                    };
                },
                importPaymentMethods(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/billing/invoicePayment/importPaymentMethods`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    accountNumber: params.accountNumber,
                                },
                            }),
                        );
                    };
                },
            },
        },
        analytics: {
            comscore() {
                return (dispatch) => {
                    const endpoint = 'rental-manager/comscore';

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,

                            // pass blank string so this request does not get proxied
                            proxyPath: '',
                        }),
                    );
                };
            },
            getYearlyOverview(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/enterpriseAnalytics/getYearlyOverview`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                listingId: params.listingId,
                                rocketshipFilter: params.rocketshipFilter,
                            },
                        }),
                    );
                };
            },
            getCoreProductInvoice(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/corePerformance/getCoreProduct`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                startDateYyyyMmDd: params.startDateYyyyMmDd,
                            },
                        }),
                    );
                };
            },
            getProperties(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/properties/get`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                startDateYyyyMmDd: params.startDateYyyyMmDd,
                            },
                        }),
                    );
                };
            },
            getBoostAdReport(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/cpmBoostStats/get`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                startDateYyyyMmDd: params.startDateYyyyMmDd,
                            },
                        }),
                    );
                };
            },
            getBoostAdPreviews(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/boostPreview/get`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                insertionOrderId: params.insertionOrderId,
                            },
                        }),
                    );
                };
            },
            overview: {
                getFilter() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/overview/getFilter`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                getListings(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/overview/getListings`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: params,
                            }),
                        );
                    };
                },
                getReviews(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/overview/getReviews`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: params,
                            }),
                        );
                    };
                },
                getMonthlyOverviewSummary(params = {}) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/overview/getMonthlyOverviewSummary`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: params,
                            }),
                        );
                    };
                },
            },
            market: {
                getMarketPropertiesSelect() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/market/getMarketProperties`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                getMarketUnitsByFloorPlanForProperty(listingAlias) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/analytics/market/getMarketUnitsByFloorPlanForProperty`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    listingAlias,
                                },
                            }),
                        );
                    };
                },
            },
        },
        singleFamilyEnterprise: {
            getContracts() {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/singleFamilyEnterprise/contracts`;
                return requester({
                    method: GET,
                    endpoint,
                });
            },
            setCustomerId(listingAlias, customerId) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/singleFamilyEnterprise/properties/customerId`;
                return requester({
                    method: POST,
                    endpoint,
                    queryParams: {
                        listingAlias,
                        customerId,
                    },
                });
            },
        },
        // posting path
        properties: {
            activate(propertyId, daysToExtend) {
                // include days to extend in params if it is not null
                const queryParams = daysToExtend !== null
                    ? {
                        propertyId,
                        daysToExtend,
                    }
                    : {
                        propertyId,
                    };

                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/activate`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams,
                        }),
                    );
                };
            },
            applicationsAccepted(propertyId) {
                const queryParams = {
                    propertyId,
                    applicationsAccepted: true,
                };

                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/applicationsAccepted`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams,
                        }),
                    );
                };
            },
            feedListingManagement: {
                getListingSources(params = {}) {
                    return (dispatch) => {
                        const endpoint =
                            `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedListingManagement/getListingSources`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    listingAlias: params.listingAlias,
                                },
                            }),
                        );
                    };
                },
            },
            amenities: {
                create(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/create`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    propertyId: data.propertyId,
                                    text: data.amenityText,
                                },
                            }),
                        );
                    };
                },
                delete(amenityId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId,
                                },
                            }),
                        );
                    };
                },
                update(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/update`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId: data.amenityId,
                                    text: data.amenityText,
                                },
                            }),
                        );
                    };
                },
            },
            apartmentAmenities: {
                // Creates and removes generalized unit amenities.
                create(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/create`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    propertyId: data.propertyId,
                                    text: data.amenityText,
                                    availableInSelectUnits: data.availableInSelectUnits,
                                },
                            }),
                        );
                    };
                },
                delete(amenityId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId,
                                },
                            }),
                        );
                    };
                },
                update(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/update`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId: data.propertyId,
                                    text: data.amenityText,
                                },
                            }),
                        );
                    };
                },
            },
            create(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/create`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyTypeCode: data.propertyTypeCode,
                                listingTypeCode: data.listingTypeCode,
                                // Optional
                                fullAddress: data.fullAddress,
                                unitNumber: data.unitNumber,
                                numUnits: data.numUnits,
                                customerId: data.customerId,
                            },
                        }),
                    );
                };
            },
            // accept optional reason param
            deactivate(propertyId, reason = null) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/deactivate`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId,
                                reason,
                            },
                        }),
                    );
                };
            },
            delete(propertyId, reason) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/delete`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId,
                                reason,
                            },
                        }),
                    );
                };
            },
            edit(data) {
                return (dispatch) => {
                    const { fieldName } = data;
                    const fieldValue = data[fieldName];

                    const queryParams = {};
                    queryParams.propertyId = data.propertyId;
                    queryParams[fieldName] = fieldValue;

                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/${fieldName}`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams,
                        }),
                    );
                };
            },
            // archive
            archive(propertyId) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/archive`;
                return requester({
                    method: POST,
                    endpoint,
                    queryParams: {
                        propertyId,
                    },
                });
            },
            // unarchive
            unArchive(propertyId) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/dearchive`;
                return requester({
                    method: POST,
                    endpoint,
                    queryParams: {
                        propertyId,
                    },
                });
            },
            autoBill(propertyId, autoBillEnabled) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/autoBillEnabled`;

                return requester({
                    method: POST,
                    endpoint,
                    queryParams: {
                        propertyId,
                        autoBillEnabled,
                    },
                });
            },
            vrModel: {
                getAll(encodedAlias) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/getAllVrModels`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    encodedAlias,
                                },
                            }),
                        );
                    };
                },
                associateModel(vrModelId, encodedAlias, encodedFloorplanKey, shouldAssociate) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/associateModel`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    vrModelId,
                                    encodedAlias,
                                    encodedFloorplanKey,
                                    shouldAssociate,
                                },
                            }),
                        );
                    };
                },
                associateListing(vrModelId, encodedAlias, shouldAssociate) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/associateListing`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    vrModelId,
                                    encodedAlias,
                                    shouldAssociate,
                                },
                            }),
                        );
                    };
                },
                publishModel(encodedAlias, vrModelId, encodedFloorplanKey, shouldPublish) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/publishModel`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    encodedAlias,
                                    vrModelId,
                                    encodedFloorplanKey,
                                    shouldPublish,
                                },
                            }),
                        );
                    };
                },
                publishListing(vrModelId, encodedAlias, shouldPublish) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/vrModel/publishListing`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    encodedAlias,
                                    shouldPublish,
                                    vrModelId,
                                },
                            }),
                        );
                    };
                },
            },
            floorPlans: {
                layouts: {
                    delete(floorPlanId) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/delete`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        floorPlanId,
                                    },
                                }),
                            );
                        };
                    },
                    upload({ formData, floorPlanId } = {}) {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/upload`;

                        return requester({
                            method: POST,
                            endpoint,
                            body: formData,
                            queryParams: {
                                floorPlanId,
                            },
                            proxyPath: FILE_PROXY_PATH,
                        });
                    },
                },
                photos: {
                    caption(data) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/caption`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        photoId: data.photoId,
                                        caption: data.caption,
                                    },
                                }),
                            );
                        };
                    },
                    rotate(photoId, clockwise) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/rotate`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        photoId,
                                        clockwise,
                                    },
                                }),
                            );
                        };
                    },
                    delete(photoId) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/delete`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        photoId,
                                    },
                                }),
                            );
                        };
                    },
                    upload({ formData, floorPlanId } = {}) {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/upload`;

                        return requester({
                            method: POST,
                            endpoint,
                            body: formData,
                            queryParams: {
                                floorPlanId,
                            },
                            proxyPath: FILE_PROXY_PATH,
                        });
                    },
                },
                units: {
                    create(floorPlanId) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/create`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        floorPlanId,
                                    },
                                }),
                            );
                        };
                    },
                    createV2(floorPlanId, params) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/createComplete`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        floorPlanId,
                                        ...params,
                                    },
                                }),
                            );
                        };
                    },
                    edit(unitId, params) {
                        return (dispatch) => {
                            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/edit`;

                            return dispatch(
                                requester({
                                    method: POST,
                                    endpoint,
                                    queryParams: {
                                        unitId,
                                        ...params,
                                    },
                                }),
                            );
                        };
                    },
                },
                get(floorPlanId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/details`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    floorPlanId,
                                },
                            }),
                        );
                    };
                },
                create(propertyId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/create`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    propertyId,
                                },
                            }),
                        );
                    };
                },
                edit(data) {
                    return (dispatch) => {
                        const { fieldName } = data;
                        const fieldValue = data[fieldName];

                        const queryParams = {};
                        queryParams.floorPlanId = data.floorPlanId;
                        queryParams[fieldName] = fieldValue;

                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/${fieldName}`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams,
                            }),
                        );
                    };
                },
                delete(floorPlanId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    floorPlanId,
                                },
                            }),
                        );
                    };
                },
            },
            getLeasesSummary(propertyId) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/leases`;

                if (!propertyId) {
                    return Promise.reject(ErrorRMW({
                        message: 'Missing required parameter, propertyId.',
                        data: propertyId,
                        code: StringConstants.MISSING_PARAM,
                    }));
                }

                return requester({
                    method: GET,
                    endpoint,
                    queryParams: {
                        propertyId,
                    },
                });
            },
            getPaymentsSummary(propertyId) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/payments`;

                if (!propertyId) {
                    return Promise.reject(ErrorRMW({
                        message: 'Missing required parameter, propertyId.',
                        data: propertyId,
                        code: StringConstants.MISSING_PARAM,
                    }));
                }

                return requester({
                    method: GET,
                    endpoint,
                    queryParams: {
                        propertyId,
                    },
                });
            },
            markAcknowledgedLandlordMessage(actionId) {
                const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/rentPayment/markAcknowledged`;

                return requester({
                    method: POST,
                    endpoint,
                    body: {
                        actionId,
                    },
                });
            },
            getListingSummary(listingAlias) {
                const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/listing`;

                if (!listingAlias) {
                    return Promise.reject(ErrorRMW({
                        message: 'Missing required parameter, listingAlias.',
                        data: listingAlias,
                        code: StringConstants.MISSING_PARAM,
                    }));
                }

                return requester({
                    method: POST,
                    endpoint,
                    queryParams: {
                        listingAlias,
                    },
                });
            },
            // add boolean to params
            getDetails(propertyId) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                propertyId,
                                includeTopDupesDetails: true,
                                includeVrModels: true,
                            },
                        }),
                    );
                };
            },
            getDetailsForEncodedAlias(encodedAlias) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/details/getPropertyDetailsForEncodedAlias`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                encodedAlias,
                            },
                        }),
                    );
                };
            },
            getOverview(propertyId) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details/overview`;

                    if (!propertyId) {
                        return Promise.reject(ErrorRMW({
                            message: 'Missing required parameter, propertyId.',
                            data: propertyId,
                            code: StringConstants.MISSING_PARAM,
                        }));
                    }

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                propertyId,
                            },
                        }),
                    );
                };
            },
            getStats(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/stats/get`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                listingId: data.listingId,
                            },
                        }),
                    );
                };
            },
            getStatus(propertyId) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/status`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                propertyId,
                            },
                        }),
                    );
                };
            },
            isConnectedExperience(propertyId) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/isConnectedExperience`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId,
                            },
                        }),
                    );
                };
            },
            leaseDuration(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/leaseDuration`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId: data.propertyId,
                                leaseDuration: data.leaseDuration,
                            },
                        }),
                    );
                };
            },
            feedToManual: {
                statuses() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/statuses`;
                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                            }),
                        );
                    };
                },
                convertAll() {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/convertAll`;
                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                            }),
                        );
                    };
                },
            },
            listings(params, useV2API = false) {
                /**
                 * Get listings for current user
                 * @param {boolean} [includeActive=true] - Include active listings
                 * @param {boolean} [includeInactive=true] - Include inactive listings
                 * @param {string} [searchText=null] - Search text to filter listings
                 * @param {boolean} [feeds=true] - Include feeds
                 * @param {boolean} [manual=true] - Include manual listings
                 * @param {boolean} [ascending=true] - Sort order asc or desc
                 * @param {string} [sort='created'] - Sort by 'created', 'location', or 'title'
                 * @param {string} [featured='all'] - Include 'all', 'paidOnly', or 'unpaidOnly'
                 * @param {string} [startKeyExclusive=null] - start key position
                 * @param {int} [limit=20] - number of listings to return
                 * @param {boolean} [includeListingDetails=true] - Include details for each listing
                 * @param {boolean} [includeListingRestrictions=true] - Include restrictions (e.g. applications geo restriction)
                 * @param {boolean} [includeMaintenancePartner=true] - Include maintenance partner information
                 * @param {boolean} [includeFeedClaimAndConvertUpdate=true] - Include Feed claim and convert update info
                 * */
                return (dispatch) => {
                    const apiPrefix = useV2API ? RENTAL_MANAGER_API_V2_PREFIX : RENTAL_MANAGER_API_V1_PREFIX;
                    const endpoint = `${apiPrefix}/users/properties/list`;

                    const listingsParams = ({
                        includeActive = Constants.PROPERTIES_ACTIVE_DEFAULT,
                        includeInactive = Constants.PROPERTIES_INACTIVE_DEFAULT,
                        searchText = Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
                        feeds = Constants.PROPERTIES_FEEDS_DEFAULT,
                        manual = Constants.PROPERTIES_MANUAL_DEFAULT,
                        ascending = Constants.PROPERTIES_ASCENDING_DEFAULT,
                        sort = Constants.PROPERTIES_SORT_DEFAULT,
                        featured = Constants.PROPERTIES_FEATURED_DEFAULT,
                        startKeyExclusive = Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
                        limit = Constants.PROPERTIES_LIMIT_DEFAULT,
                        includeListingDetails = Constants.PROPERTIES_INCLUDE_DETAILS_DEFAULT,
                        includeListingRestrictions = Constants.PROPERTIES_INCLUDE_RESTRICTIONS_DEFAULT,
                        includeMaintenancePartner = Constants.PROPERTIES_INCLUDE_MAINTENANCE_PARTNER_DEFAULT,
                        includeArchived = Constants.PROPERTIES_INCLUDE_ARCHIVED,
                        includeUnarchived = Constants.PROPERTIES_INCLUDE_UNARCHIVED,
                        includeFeedClaimAndConvertUpdate = Constants.PROPERTIES_INCLUDE_FEED_CLAIM_AND_CONVERT_UPDATE,
                    } = {}) => ({
                        includeActive: Boolean(includeActive),
                        includeInactive: Boolean(includeInactive),
                        searchText: String(searchText),
                        feeds: Boolean(feeds),
                        manual: Boolean(manual),
                        ascending: Boolean(ascending),
                        sort: String(sort),
                        featured: String(featured),
                        startKeyExclusive: String(startKeyExclusive),
                        limit: Number(limit),
                        includeListingDetails: Boolean(includeListingDetails),
                        includeListingRestrictions: Boolean(includeListingRestrictions),
                        includeMaintenancePartner: Boolean(includeMaintenancePartner),
                        includeFeedClaimAndConvertUpdate: Boolean(includeFeedClaimAndConvertUpdate),
                        includeArchived: Boolean(includeArchived),
                        includeUnarchived: Boolean(includeUnarchived),
                    });
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: listingsParams(params),
                            timeout: 30000,
                        }),
                    );
                };
            },
            listingsCount() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listings/count`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },

            /**
             * Get listings based on property type, location, and brand.
             *
             * @param {string} type - The type of listing to fetch
             * @param {object} params - The query params to pass to the endpoint
             *   @param {boolean} [startKeyExclusive=null] - return response after listing id
             *   @param {boolean} [limit=null] - Limit the number of responses
             **/
            listingInt(type, params) {
                // type for listings is statuses
                if (type === SelectPropertyConstants.LISTINGS) {
                    type = 'statuses';
                }

                const queryParams = ({
                    startKeyExclusive = '',
                    limit = 20,
                }) => ({
                    startKeyExclusive: String(startKeyExclusive),
                    limit: Number(limit),
                });

                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listings/${type}`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: queryParams(params),
                        }),
                    );
                };
            },
            logos: {
                delete(propertyId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/logos/delete`;
                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    propertyId,
                                },
                            }),
                        );
                    };
                },
                upload({ formData, propertyId } = {}) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/logos/upload`;

                    return requester({
                        method: POST,
                        endpoint,
                        body: formData,
                        queryParams: {
                            propertyId,
                        },
                        proxyPath: FILE_PROXY_PATH,
                    });
                },
            },
            pets(data) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/pets`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: data,

                        }),
                    );
                };
            },
            photos: {
                caption(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/caption`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    photoId: data.photoId,
                                    caption: data.caption,
                                },
                            }),
                        );
                    };
                },
                sort(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/photos/rank`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    propertyId: data.propertyId,
                                },
                                body: {
                                    ranks: data.photosArray,
                                },
                            }),
                        );
                    };
                },
                delete(photoId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    photoId,
                                },
                            }),
                        );
                    };
                },
                upload({ formData, propertyId } = {}) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/upload`;

                    return requester({
                        method: POST,
                        endpoint,
                        body: formData,
                        queryParams: {
                            propertyId,
                        },
                        proxyPath: FILE_PROXY_PATH,
                    });
                },
                rotate(photoId, clockwise) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/rotate`;

                        return dispatch(
                            requester({
                                method: GET,
                                endpoint,
                                queryParams: {
                                    photoId,
                                    clockwise,
                                },
                            }),
                        );
                    };
                },
            },

            syndication(propertyId) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/syndication`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                propertyId,
                            },
                        }),
                    );
                };
            },
            unitAmenities: {
                create(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/create`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    floorPlanId: data.floorPlanId,
                                    text: data.amenityText,
                                    availableInSelectUnits: data.availableInSelectUnits,
                                },
                            }),
                        );
                    };
                },
                delete(amenityId) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/delete`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId,
                                },
                            }),
                        );
                    };
                },
                update(data) {
                    return (dispatch) => {
                        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/update`;

                        return dispatch(
                            requester({
                                method: POST,
                                endpoint,
                                queryParams: {
                                    amenityId: data.amenityId,
                                    text: data.amenityText,
                                },
                            }),
                        );
                    };
                },
            },
            paidInclusion: {
                initiateCreditCardCapture(listingId, metroidProductId) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/initiateCreditCardCapture`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: { listingId, metroidProductId },
                    });
                },
                initiateCreditCardCharge(listingId, metroidProductId) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/initiateCreditCardCharge`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: { listingId, metroidProductId },
                    });
                },
                verifyCreditCardCapture(apiAccessToken) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/verifyCreditCardCapture`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: { apiAccessToken },
                    });
                },
                verifyPaymentAndActivateListing(listingId, hostApp, includeDefaultPaymentMethod = false) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/verifyPaymentAndActivateListing`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: {
                            listingId,
                            hostApp,
                            includeDefaultPaymentMethod,
                        },
                    });
                },
                defaultPaymentMethod(listingId) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/getDefaultPaymentMethod`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: {
                            listingId,
                        },
                    });
                },
                getPrice() {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/getPrice`;

                    return requester({
                        endpoint,
                        method: GET,
                    });
                },
                details(listingId) {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/paidInclusion/details`;

                    return requester({
                        endpoint,
                        method: GET,
                        queryParams: {
                            listingId,
                        },
                    });
                },
            },
        },
        termsAndConditions: {
            markAcceptedTerms(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/termsAndConditions/markAcceptedTerms`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body: params,
                        }),
                    );
                };
            },
        },
        emailVerification: {
            unverifiedEmailFeedListings(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/unverifiedEmailFeedListings`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: data,
                        }),
                    );
                };
            },
            sendSecurityToken(data = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/sendSecurityToken`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: data,
                        }),
                    );
                };
            },
            verifySecurityToken(securityToken) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/verifySecurityToken`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                securityToken,
                            },
                        }),
                    );
                };
            },
        },
        scheduler: {
            update(listingId, schedule) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/scheduler/update`;

                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                listingId,
                            },
                            body: schedule,
                        }),
                    );
                };
            },
        },
        company: {
            getListingsForEmail(email) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getListingsForEmail`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                email,
                            },
                        }),
                    );
                };
            },
            updateCompanyName(companyName) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/updateCompanyName`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                companyName,
                            },
                        }),
                    );
                };
            },
            declineUserManagementInvitation() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/companyOnboarding/declineUserManagementInvitation`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getUserManagementInvitation() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/companyOnboarding/getUserManagementInvitation`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getCompanyUsers() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserManagementCompanyUsers`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getUnrecognizedUsers() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUnrecognizedUsers`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getUserCompanyStats() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserCompanyStats`;

                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getUserSettings(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserProfiles`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                userBrand: params.userBrand,
                                userToken: params.userToken,
                            },
                        }),
                    );
                };
            },
            getPendingProfile(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getPendingProfile`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                email: params.email,
                            },
                        }),
                    );
                };
            },
            getPendingUserProperties(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getPendingUserProperties`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                email: params.email,
                            },
                        }),
                    );
                };
            },
            getUserManagementProfiles() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserManagementProfiles`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            getUserCompanyProperties(params = {}) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserCompanyProperties`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                userBrand: params.userBrand,
                                userToken: params.userToken,
                            },
                        }),
                    );
                };
            },
            updateUserSettings(body) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/updateUserSettings`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            verifyEmail(params) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/verifyEmail`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                email: params.email,
                            },
                        }),
                    );
                };
            },
            createUserInvitation(body) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/createUserInvitation`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            updateUserInvitation(body) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/updateUserInvitation`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            body,
                        }),
                    );
                };
            },
            resendInviteEmail(email) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/resendInviteEmail`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                email,
                            },
                        }),
                    );
                };
            },
            onboardPendingUser() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/companyOnboarding/onboardPendingUser`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                        }),
                    );
                };
            },
            cancelInvitation(email) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/cancelUserInvitation`;
                    return dispatch(
                        requester({
                            method: POST,
                            endpoint,
                            queryParams: {
                                email,
                            },
                        }),
                    );
                };
            },
            getCompanyProperties() {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getCompanyProperties`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                        }),
                    );
                };
            },
            deactivateUser(userBrand, userToken) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/deactivateUser`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                userBrand,
                                userToken,
                            },
                        }),
                    );
                };
            },
            getActivityHistory(startTimestamp) {
                return (dispatch) => {
                    const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getActivityHistory`;
                    return dispatch(
                        requester({
                            method: GET,
                            endpoint,
                            queryParams: {
                                startTimestamp,
                            },
                        }),
                    );
                };
            },
        },
    },

    payments: {
        createAccount(data) {
            return (dispatch) => {
                const endpoint = 'rent-payment/api/v1/account';
                return dispatch(
                    requester({
                        method: POST,
                        path: endpoint,
                        body: data,
                    }),
                );
            };
        },
        updateAccount(accountId, data) {
            return (dispatch) => {
                const endpoint = `rent-payment/api/v1/account/${accountId}`;
                return dispatch(
                    requester({
                        method: POST,
                        path: endpoint,
                        body: data,
                    }),
                );
            };
        },
        getVerificationStatus(accountId) {
            return (dispatch) => {
                const endpoint = `rent-payment/api/v1/account/${accountId}`;
                return dispatch(
                    requester({
                        method: GET,
                        path: endpoint,
                    }),
                );
            };
        },
    },
    marketing: {
        smsAppLead(phoneNumber, campaignId) {
            return (dispatch) => {
                const endpoint = `${RENTAL_GROWTH_API_V1_PREFIX}/campaign/phone`;
                return dispatch(
                    requester({
                        method: GET,
                        endpoint,
                        queryParams: {
                            phoneNumber,
                            campaignId,
                        },
                        proxyPath: RENTAL_GROWTH_PROXY_PATH,
                    }),
                );
            };
        },
        fetchByQuery(query) {
            return (dispatch) => {
                const endpoint = `${RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX}/listing`;

                // check error
                if (query instanceof Error) {
                    return Promise.reject(ErrorRMW({
                        message: query.message,
                        data: '',
                    }));
                }

                return dispatch(
                    requester({
                        method: POST,
                        endpoint,
                        body: query,
                        proxyPath: RENTAL_GROWTH_PROXY_PATH,
                    }),
                );
            };
        },
    },
    zillowSubscription: {
        getSubscriptionStatus(subscriptionType) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/zillowSubscription/getSubscriptionStatus`;
            return requester({
                method: GET,
                endpoint,
                queryParams: {
                    subscriptionType,
                },
            });
        },
        updateSubscriptionStatus(subscriptionType, enrolled) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/zillowSubscription/updateSubscriptionStatus`;
            return requester({
                method: POST,
                endpoint,
                queryParams: {
                    subscriptionType,
                    enrolled,
                },
            });
        },
    },
};

export default api;
